import styles from "./Modals.module.scss";
import React, { useEffect, useRef, useState } from "react";
import type { Destination } from "@whatslly/types/messages-server/destinations";
import { DESTINATION_INTEGRATION_TYPES } from "@whatslly/types/messages-server/destinations";
import { FormElement } from "@whatslly/ui-components/v2/FormElements/Form";
import { FormikErrors, FormikProps } from "formik";
import type { PartialRecord } from "@whatslly/commons/typescriptUtils";
import { FormTextInput } from "@whatslly/ui-components/v2/FormElements/FormTextInput";
import { Label } from "@whatslly/ui-components/v2/BaseElements/Label";
import { useTranslation } from "react-i18next";
import { useLocalStoreWithInjectedDependencies } from "@whatslly/commons/di";
import { DestinationsStore } from "../DestinationsStore";
import { DestinationsModalButtons } from "../DestinationsModalButtons";
import type { DestinationFormProps, FormDestinationDataType } from "../utils/DestinationsListTypes";
const isValidUrl = (urlString: string) => {
  const urlPattern = new RegExp(
    "^(https?:\\/\\/)?" + // validate protocol
      "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" + // validate domain name
      "((\\d{1,3}\\.){3}\\d{1,3}))" + // validate OR ip (v4) address
      "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" + // validate port and path
      "(\\?[;&a-z\\d%_.~+=-]*)?" + // validate query string
      "(\\#[-a-z\\d_]*)?$",
    "i"
  ); // validate fragment locator
  return urlPattern.test(urlString);
};

const BASE_DATA: Destination = {
  type: "forcepointDlpDetector" as const,
  integrationType: DESTINATION_INTEGRATION_TYPES.DLP_DETECTORS,
  settings: {
    serverUrl: "",
  },
};
type FormDataType = FormDestinationDataType<"forcepointDlpDetector">;

export const ForcepointDlpDetectorModalForm: React.FC<DestinationFormProps<"forcepointDlpDetector">> = ({ settings, onSubmitForm }) => {
  const { t } = useTranslation();
  const [formikErrorHasErrors, setFormikErrorHasErrors] = useState(true);
  const formikContext = useRef<FormikProps<FormDataType>>(null);
  const store = useLocalStoreWithInjectedDependencies(DestinationsStore);

  const validate = async (values: FormDataType) => {
    const errors: FormikErrors<PartialRecord<keyof FormDataType, string>> = {};
    if (!values.serverUrl) errors.serverUrl = t("field_is_required");

    if (!errors.serverUrl && !isValidUrl(values.serverUrl)) errors.serverUrl = t("invalid_url");

    setFormikErrorHasErrors(Object.keys(errors).length > 0);
    return errors;
  };

  const getFormValues = () => {
    const data = (formikContext.current?.values ?? {}) as FormDataType;
    return { ...BASE_DATA, settings: { ...data, serverUrl: data.serverUrl } };
  };

  const onSubmit = async () => {
    store.testConnection(t, getFormValues());
    const res = await store.testConnectionPromise;
    if (res?.success) {
      onSubmitForm(getFormValues());
    }
  };

  useEffect(() => {
    validate(getFormValues().settings);
  }, []);

  return (
    <FormElement initialValues={settings || BASE_DATA.settings} validate={validate} onSubmit={onSubmit} formikRef={formikContext}>
      <div className={styles.field}>
        <Label className={styles.filedLabel} text={"Server url"} required />
        <FormTextInput name={"serverUrl"} paddingInline="none" />
      </div>
      <DestinationsModalButtons formikContext={formikContext} formikErrorHasErrors={formikErrorHasErrors} store={store} />
    </FormElement>
  );
};
